import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import useAuthStore from "store/useAuthStore";
import { cookiesRefreshTokenName, cookiesTokenName } from "utils/helper";
import { loginData } from "./auth";

const prefix = "/api"
const axiosConfig = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL + prefix,
    headers: {
        "accept": "application/json",
    },
    transformRequest: [(data, headers) => {
        // Check if Content-Type is explicitly set in the request config
        if (!headers['Content-Type']) {
            // Default to application/json if no Content-Type is specified
            headers['Content-Type'] = 'application/json';
            return JSON.stringify(data);
        }

        // Handle different Content-Types
        switch (headers['Content-Type'].toLowerCase()) {
            case 'application/json':
                return JSON.stringify(data);
            case 'application/x-www-form-urlencoded':
                return new URLSearchParams(data).toString();
            case 'multipart/form-data':
                return data; // FormData will be handled automatically
            default:
                return data;
        }
    }]
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

axiosConfig.interceptors.request.use(
    async (config) => {
        const token = Cookies.get(cookiesTokenName);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject({ status: error.response?.status, data: error.response?.data });
    }
);

axiosConfig.interceptors.response.use(
    (response) => {
        return {
            status: response.status,
            data: response.data
        };
    },
    async (error) => {
        if (axios.isAxiosError(error) && error.config) {
            const originalRequest = error.config;
            if (!error.config.url?.includes("login") && error.response?.status === 401) {
                Cookies.remove(cookiesTokenName);

                // Only queue the request if we're already refreshing
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject })
                    })
                        .then((token) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`
                            return axiosConfig(originalRequest)
                        })
                        .catch((err) => Promise.reject(err))
                }

                // If this request hasn't been retried and we're not refreshing yet
                if (!originalRequest._retry) {
                    originalRequest._retry = true
                    isRefreshing = true

                    try {
                        const refreshToken = Cookies.get(cookiesRefreshTokenName)
                        if (!refreshToken) {
                            throw new Error("No refresh token available")
                        }

                        const response = await axios.post(
                            `${process.env.REACT_APP_BASE_URL}/api/auth/refresh`,
                            { ...loginData, refresh_token: refreshToken, scopes: '*' }
                        );
                        const { access_token: newAccessToken, refresh_token: refresh } = response.data;
                        Cookies.set(cookiesTokenName, newAccessToken, { expires: 365, secure: true, sameSite: "None", path: "/" });
                        Cookies.set(cookiesRefreshTokenName, refresh, { expires: 365, secure: true, sameSite: "None", path: "/" });

                        axiosConfig.defaults.headers.Authorization = `Bearer ${newAccessToken}`
                        processQueue(null, newAccessToken)

                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
                        return axiosConfig(originalRequest)

                    } catch (refreshError) {
                        // If refresh token fails, clear tokens and reject all queued requests
                        processQueue(refreshError, null);
                        isRefreshing = false;
                        Cookies.remove(cookiesTokenName);
                        Cookies.remove(cookiesRefreshTokenName);

                        useAuthStore.getState().logout()
                        toast.error("Token expired, please log in again.", { toastId: "tokenExpired" });
                        return Promise.reject(refreshError);
                    }
                } else {
                    // If we've already tried to refresh for this request
                    isRefreshing = false;
                    Cookies.remove(cookiesTokenName);
                    Cookies.remove(cookiesRefreshTokenName);
                    useAuthStore.getState().logout();
                    toast.error("Token expired, please log in again.", { toastId: "tokenExpired" });
                    return Promise.reject({
                        status: 401,
                        data: { error: "Token expired, please log in again." }
                    });
                }
            }
        } else if (error.response?.status === 500) {
            return Promise.reject({
                status: error.response?.status,
                data: { error: "Server error, please try again later." }
            });
        } else if (error.response?.status === 404) {
            return Promise.reject({
                status: error.response?.status,
                data: { error: "Requested resource not found." }
            });
        }
        return Promise.reject({
            status: error.response?.status,
            data: error.response?.data || { error: "Request failed, please try again later." }
        });
    }
);

export default axiosConfig;